import { useTranslation } from "react-i18next"
import Typography from "@mui/material/Typography"
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded"
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded"
import WifiTetheringIcon from "@mui/icons-material/WifiTethering"
import WifiTetheringErrorIcon from "@mui/icons-material/WifiTetheringError"
import DeviceUnknownIcon from "@mui/icons-material/DeviceUnknown"
import { Tooltip } from "@mui/material"

import type { PropsWithChildren } from "react"

import type { DeviceReportingStatus } from "types/device.types"

import StatusBox from "../common/StatusBox"

type ReportingStatusVariant = "label" | "tag" | "legend" | "icon"

const STATUS_VISUALS: Record<
  DeviceReportingStatus,
  { Icon: typeof ShowChartRoundedIcon; key: string }
> = {
  unknown: { Icon: DeviceUnknownIcon, key: "UNKNOWN" },
  reporting: { Icon: ShowChartRoundedIcon, key: "REPORTING" },
  active: { Icon: WifiTetheringIcon, key: "ACTIVE" },
  intermittent: { Icon: WifiTetheringErrorIcon, key: "INTERMITTENT" },
  inactive: { Icon: HighlightOffRoundedIcon, key: "INACTIVE" },
}

interface TooltipWrapperProps {
  title: string
  variant: ReportingStatusVariant
}

const TooltipWrapper = ({
  children,
  title,
  variant,
}: PropsWithChildren<TooltipWrapperProps>) => {
  return (
    <>
      {variant !== "legend" ? (
        <Tooltip title={title}>
          <span>{children}</span>
        </Tooltip>
      ) : (
        <span>{children}</span>
      )}
    </>
  )
}

interface ReportingStatusProps {
  status: DeviceReportingStatus
  variant?: ReportingStatusVariant
}

const ReportingStatus = ({ status, variant = "label" }: ReportingStatusProps) => {
  const { t } = useTranslation()
  const isLegend = variant === "legend"

  // Anything unexpected reads as inactive, as it always has.
  const resolved = STATUS_VISUALS[status] ? status : "inactive"
  const { Icon, key } = STATUS_VISUALS[resolved]
  const label = t(`reporting_status.${key}`)
  const description = t(`device.${key}_STATUS`)

  // The icon alone has no text beside it, so the tooltip names the status.
  if (variant === "icon") {
    return (
      <TooltipWrapper
        title={t("reporting_status.WITH_STATUS", { status: label })}
        variant={variant}
      >
        <StatusBox status={resolved} variant={variant}>
          <Icon fontSize="small" />
        </StatusBox>
      </TooltipWrapper>
    )
  }

  return (
    <TooltipWrapper title={description} variant={variant}>
      <StatusBox status={resolved} variant={variant}>
        <Icon fontSize="small" />
        <Typography component="p" variant="body2">
          {label}
          {isLegend && ` - ${description}`}
        </Typography>
      </StatusBox>
    </TooltipWrapper>
  )
}

export default ReportingStatus
